/**
 * STATIC reading plan catalog (templates). 100% offline: derived
 * from bundled book/chapter metadata (`bible-books-data`), without
 * dedicated table or network. Each plan `id` is a STABLE slug — key
 * `plan_id` of synchronized progress rows (`bible_reading_progress`),
 * so must NEVER be renamed (adding new plans is safe).
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { bookChapters } from "@/utils/bible-books-data";
import { type PlanDay, type PlanReading, splitIntoDays } from "../logic";

interface ReadingPlanTemplate {
  /** Slug estable (clave de `bible_reading_progress.plan_id`). */
  id: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  days: PlanDay[];
}

/* oxlint-disable no-magic-numbers -- Canonical book IDs and plan durations */

/** Chapters of given books, in canonical order. */
function chaptersOf(bookIds: readonly number[]): PlanReading[] {
  const readings: PlanReading[] = [];
  for (const book of bookIds) {
    const count = bookChapters[book] ?? 0;
    for (let chapter = 1; chapter <= count; chapter += 1) {
      readings.push({ book, chapter });
    }
  }
  return readings;
}

function range(from: number, to: number): number[] {
  const ids: number[] = [];
  for (let i = from; i <= to; i += 1) {
    ids.push(i);
  }
  return ids;
}

const JOHN = 43;
const PSALMS = 19;
const PROVERBS = 20;
const MATTHEW = 40;
const REVELATION = 66;

/**
 * Available plans. Order = display order (from shortest/lightest to
 * ambicioso).
 */
export const READING_PLANS: ReadingPlanTemplate[] = [
  {
    id: "john-21",
    title: msg`The Gospel of John in 21 days`,
    description: msg`One chapter a day through John's account of Jesus — ideal as a first plan.`,
    days: splitIntoDays(chaptersOf([JOHN]), 21),
  },
  {
    id: "proverbs-31",
    title: msg`Proverbs in 31 days`,
    description: msg`A chapter of wisdom each day for a month.`,
    days: splitIntoDays(chaptersOf([PROVERBS]), 31),
  },
  {
    id: "psalms-30",
    title: msg`Psalms in 30 days`,
    description: msg`Five psalms a day: the whole Psalter in a month of prayer.`,
    days: splitIntoDays(chaptersOf([PSALMS]), 30),
  },
  {
    id: "gospels-40",
    title: msg`The four Gospels in 40 days`,
    description: msg`Matthew, Mark, Luke and John over forty days.`,
    days: splitIntoDays(chaptersOf(range(MATTHEW, JOHN)), 40),
  },
  {
    id: "nt-90",
    title: msg`New Testament in 90 days`,
    description: msg`The entire New Testament in three months, about three chapters a day.`,
    days: splitIntoDays(chaptersOf(range(MATTHEW, REVELATION)), 90),
  },
];

/* oxlint-enable no-magic-numbers */

export function getPlanTemplate(planId: string): ReadingPlanTemplate | undefined {
  return READING_PLANS.find((plan) => plan.id === planId);
}
